import React, { useContext, useMemo } from 'react'
import DirectMarketingBanner from 'src/Libs/installation-extensions/components/luox/direct-marketing/LuoxDirectMarketingBanner'
import { AllFlowSettingPages, AllSettingPages, Banner, BannerKey, BannerLocations, BannerType, FlowAdvancedSettingsTabs, Priority } from '../types'
import useDirectMarketingBannerConditions from 'src/Libs/installation-extensions/hooks/luox/use-direct-marketing-banner-conditions'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import OfflineInstallationBanner from '../../offline-mode/components/OfflineInstallationBanner'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { EMobilityAccessRightsContext } from 'src/Libs/system-settings/provider/EMobilityAccessRightsContext'
import ReadOnlyBanner from 'src/Libs/system-settings/component/ReadOnlyBanner'
import SunModeDisabledBanner from '../../wallboxes/components/sun-mode/SunModeDisabledBanner'
import useSunModeDisabledBannerConditions from '../../wallboxes/hooks/use-sunmode-disabled-banner-conditions'
import { UserSettings } from 'src/Libs/user/types'
import { useUserSettings } from 'src/Hooks'
import { usePrevious } from '@e3dc-react/shell/Hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDynamicPowerPriceAlertBannerConditions } from '../../dynamic-power-prices/hooks/use-variable-dynamic-power-price-alert-banner-conditions'
import ActiveVariableDynamicPowerPriceAlertBanner from '../../dynamic-power-prices/components/alert/ActiveVariableDynamicPowerPriceAlertBanner'
import { EmsPromotionBanner } from 'src/Libs/dynamic-power-prices/components/dynamic-ems'
import { useIsDynamicEmsAccessible, useShouldShowEmsBanner } from 'src/Libs/dynamic-power-prices'
import { InstallationStatementDeadlineOverviewBanner, InstallationStatementDeadlineDashboardBanner } from 'src/Libs/installations/components'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useUserRoles } from 'src/Hooks/user'
import { CountryInfoMissingBanner, useFetchCountryInfo, NewEMobilityUnavailableBanner, EMobilityTourBanner } from 'src/Libs/e-mobility/shared'
import { EMobilityImplementationCheckStatus, useSelectEMobilityImplementation } from 'src/Libs/e-mobility/use-select-e-mobility-implementation'
import { useShouldShowEMobilityTourBanner } from 'src/Libs/e-mobility/guided-tour'
import { NewVehicleManufacturesBanner } from 'src/Libs/e-mobility/vehicle-integration/components/banners'
import { isFlowTheme } from 'src/Libs/utils'
import { AI360ReleaseBanner } from 'src/Libs/ai-360/components'
import { isSoftwareVersionSupported, useSoftwareVersion } from 'src/Libs/home-power-station'
import EonBanner from 'src/Libs/installation-extensions/components/eon/EonBanner'
import { useFarmContext } from 'src/Elements/Farm'
import { checkIsDeadlineApproaching, useGetDeviceLinks } from 'src/Libs/installations'
import {
  Ai360TariffsBanner,
  ContinueAi360TariffOrderBanner,
  useAi360Access,
  useInstallationExtensionData,
  getContractStates,
  ExtensionTypes,
} from 'src/Libs/installation-extensions'

type UseBanner = {
  banners: Banner[]
  isLoading: boolean
}

/**
 * This hook adds banners to the banner queue if the conditions are met
 */
const useBanner = (): UseBanner => {
  const installation = useTypedSelector(state => state.storage.installation)
  const installationId = installation?.id
  const isStorageLoading = useTypedSelector(state => state.storage.storageLoading)
  const prevInstallationId = usePrevious(installation?.id)
  const showDirectMarketingBanner = useDirectMarketingBannerConditions()
  const { isStorageOnline, areAllClientsOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()
  const { hasEMobilityWritingRights } = useContext(EMobilityAccessRightsContext)
  const isSunModeDisabledBannerVisible = useSunModeDisabledBannerConditions()
  const { showEmsBanner } = useShouldShowEmsBanner()
  const { userSettings, isUserSettingsLoading } = useUserSettings()
  const { isServiceUser } = useUserRoles()
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const { deviceLinks, isLoading: isDeviceLinkLoading } = useGetDeviceLinks(installationId?.toString())
  const userData = useTypedSelector(state => state.user.userData)
  const { hasAi360TariffBannerAccess, hasAi360PowerFixAccess, hasAi360PowerShareAccess } = useAi360Access(installation, userData)
  const { installationExtensionsState } = useInstallationExtensionData(installationId?.toString())
  const { ai360PowerFixState, ai360PowerShareState } = getContractStates(installationExtensionsState)
  const orderedTariffType = userSettings.ai360SmartMeterOrder?.tariffType
  const isOrderedTariffConnected =
    (orderedTariffType === ExtensionTypes.Ai360PowerFix && ai360PowerFixState === 'active') ||
    (orderedTariffType === ExtensionTypes.Ai360PowerShare && ai360PowerShareState === 'active')
  const hasOrderedTariffAccess =
    (orderedTariffType === ExtensionTypes.Ai360PowerFix && hasAi360PowerFixAccess) ||
    (orderedTariffType === ExtensionTypes.Ai360PowerShare && hasAi360PowerShareAccess)
  const isOrderedOnCurrentInstallation = userSettings.ai360SmartMeterOrder?.installationId === installationId
  /* Once the ai360 tariff the user was ordering a smart meter for is connected, the reminder banner is no longer needed.
   * The reminder is also scoped to the installation the order was initiated from, so it doesn't follow the user to others,
   * and only shown while the ordered tariff is available, since the banner reopens that tariff's details. */
  const shouldShowContinueAi360TariffOrderBanner =
    Boolean(userSettings.ai360SmartMeterOrder) && isOrderedOnCurrentInstallation && !isOrderedTariffConnected && hasOrderedTariffAccess

  const incompleteDevicesWithDeadlineApproaching = useMemo(
    () => (deviceLinks ?? []).filter(deviceLink => checkIsDeadlineApproaching(deviceLink)),
    [deviceLinks]
  )

  const { status: eMobilityImplementationCheckStatus, isLoading: isEMobilityImplementationLoading } = useSelectEMobilityImplementation({
    installationId,
    skipCheck: Boolean(installation?.decommissionedAt),
  })
  const {
    featureFlags: { dynamicEmsMinimalVersion },
  } = useFeatureFlagsContext()
  const { softwareVersion, isLoading: isSoftwareVersionLoading } = useSoftwareVersion()
  const isDynamicEmsSupported = isSoftwareVersionSupported(dynamicEmsMinimalVersion, softwareVersion)

  const shouldShowEmsBanner = showEmsBanner && isDynamicEmsSupported && !isSoftwareVersionLoading

  const isActiveDynamicPowerPriceAlertBannerVisible = useDynamicPowerPriceAlertBannerConditions()
  const { country, isCountryInfoLoading } = useFetchCountryInfo(
    installation?.countryCode ?? '',
    installationId,
    Boolean(installation?.decommissionedAt)
  )
  const shouldShowEMobilityTourBanner = useShouldShowEMobilityTourBanner()

  const directMarketingBanner: Banner = useMemo(() => {
    return {
      key: BannerKey.DirectMarketing,
      content: <DirectMarketingBanner />,
      priority: Priority.Low,
      type: BannerType.Info,
      locations: [BannerLocations.Dashboard],
    }
  }, [])

  const offlineInstallationBanner: Banner = useMemo(
    () => ({
      key: BannerKey.OfflineInstallationBanner,
      content: <OfflineInstallationBanner isServiceView={false} />,
      priority: Priority.Highest,
      type: BannerType.Warning,
      locations: [BannerLocations.Dashboard, ...AllSettingPages, ...AllFlowSettingPages, ...FlowAdvancedSettingsTabs, BannerLocations.SystemStatus],
    }),
    []
  )

  const offlineInstallationBannerServiceView: Banner = useMemo(
    () => ({
      key: BannerKey.OfflineInstallationBanner,
      content: <OfflineInstallationBanner isServiceView={true} />,
      priority: Priority.Highest,
      type: BannerType.Warning,
      locations: [BannerLocations.SystemDetails],
    }),
    []
  )

  const readOnlyBanner: Banner = useMemo(() => {
    return {
      key: BannerKey.ReadOnlyBanner,
      content: <ReadOnlyBanner />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [
        ...AllSettingPages,
        ...AllFlowSettingPages,
        ...FlowAdvancedSettingsTabs,
        BannerLocations.Dashboard,
        BannerLocations.SystemStatus,
        BannerLocations.Documentation,
        BannerLocations.RightsAdministration,
      ],
    }
  }, [])

  const readOnlyBannerEmobility: Banner = useMemo(() => {
    return {
      key: BannerKey.ReadOnlyBannerEmobility,
      content: <ReadOnlyBanner />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [BannerLocations.Emobility],
    }
  }, [])

  const activeVariableDynamicPowerPriceAlertBanner: Banner = useMemo(
    () => ({
      key: BannerKey.ActiveVariableDynamicPowerPriceAlertBanner,
      content: <ActiveVariableDynamicPowerPriceAlertBanner />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [
        BannerLocations.Dashboard,
        BannerLocations.SettingsSmartCharge,
        BannerLocations.SettingsTariffs,
        BannerLocations.SettingsWallbox,
        BannerLocations.FlowSettingsAdvancedSettingsOverview,
        BannerLocations.FlowSettingsAdvancedSettingsChargingStation,
      ],
    }),
    []
  )

  const emsPromotionBanner: Banner = useMemo(
    () => ({
      key: BannerKey.DynamicEmsBanner,
      content: <EmsPromotionBanner />,
      priority: Priority.High,
      type: BannerType.Info,
      locations: [BannerLocations.Dashboard],
    }),
    []
  )

  const sunModeDisabledBanner: Banner = useMemo(
    () => ({
      key: BannerKey.SunModeDisabledBanner,
      content: <SunModeDisabledBanner />,
      priority: Priority.Highest,
      type: BannerType.Info,
      locations: [
        BannerLocations.Dashboard,
        BannerLocations.SettingsSmartCharge,
        BannerLocations.SettingsWallbox,
        BannerLocations.FlowSettingsAdvancedSettingsOverview,
        BannerLocations.FlowSettingsAdvancedSettingsChargingStation,
      ],
    }),
    []
  )

  const installationStatementDeadlineOverviewBanner: Banner = useMemo(
    () => ({
      key: BannerKey.InstallationStatementDeadlineOverviewBanner,
      content: <InstallationStatementDeadlineOverviewBanner />,
      priority: Priority.Highest,
      type: BannerType.Warning,
      locations: [BannerLocations.InstallerDashboard, BannerLocations.ServiceDashboard, BannerLocations.OwnerOverview],
    }),
    []
  )

  const installationStatementDeadlineDashboardBanner: Banner = useMemo(
    () => ({
      key: BannerKey.InstallationStatementDeadlineDashboardBanner,
      content: <InstallationStatementDeadlineDashboardBanner />,
      priority: Priority.Highest,
      type: BannerType.Warning,
      locations: [BannerLocations.Dashboard],
    }),
    []
  )

  const newVehicleManufacturersBanner: Banner = useMemo(
    () => ({
      key: BannerKey.NewVehicleManufacturersBanner,
      content: <NewVehicleManufacturesBanner />,
      priority: Priority.High,
      type: BannerType.Info,
      locations: [BannerLocations.Dashboard],
    }),
    []
  )

  const ai360ReleaseBanner: Banner = useMemo(
    () => ({
      key: BannerKey.AI360ReleaseBanner,
      content: <AI360ReleaseBanner />,
      priority: Priority.High,
      type: BannerType.Info,
      locations: [BannerLocations.Dashboard],
    }),
    []
  )

  const sessionsInfoMissingBanner: Banner = useMemo(
    () => ({
      key: BannerKey.EmobilityCountryInfoMissingSessionBanner,
      content: <CountryInfoMissingBanner bannerKey={BannerKey.EmobilityCountryInfoMissingSessionBanner} />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [BannerLocations.EmobilityCountryInfoMissingSessions],
    }),
    []
  )

  const wallboxesInfoMissingBanner: Banner = useMemo(
    () => ({
      key: BannerKey.EmobilityCountryInfoMissingWallboxesBanner,
      content: <CountryInfoMissingBanner bannerKey={BannerKey.EmobilityCountryInfoMissingWallboxesBanner} />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [BannerLocations.EmobilityCountryInfoMissingWallboxes],
    }),
    []
  )

  const tariffsInfoMissingBanner: Banner = useMemo(
    () => ({
      key: BannerKey.EmobilityCountryInfoMissingTariffsBanner,
      content: <CountryInfoMissingBanner bannerKey={BannerKey.EmobilityCountryInfoMissingTariffsBanner} />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [BannerLocations.EmobilityCountryInfoMissingTariffs],
    }),
    []
  )

  const newEMobilityUnavailableBanner: Banner = useMemo(
    () => ({
      key: BannerKey.EmobilityNewBackendUnavailableBanner,
      content: <NewEMobilityUnavailableBanner />,
      priority: Priority.High,
      type: BannerType.Warning,
      locations: [BannerLocations.EmobilityRoot],
    }),
    []
  )

  const eMobilityTourBanner: Banner = useMemo(
    () => ({
      key: BannerKey.EmobilityTourBanner,
      content: <EMobilityTourBanner />,
      priority: Priority.High,
      type: BannerType.Info,
      locations: [BannerLocations.EmobilityTour],
    }),
    []
  )

  const eonBanner: Banner = useMemo(() => {
    return {
      key: BannerKey.EonOfferBanner,
      content: <EonBanner />,
      priority: Priority.High,
      type: BannerType.Info,
      locations: [BannerLocations.Dashboard],
    }
  }, [])

  const ai360TariffsBanner: Banner = useMemo(() => {
    return {
      key: BannerKey.Ai360TariffsBanner,
      content: <Ai360TariffsBanner />,
      priority: Priority.High,
      type: BannerType.Info,
      locations: [BannerLocations.Dashboard],
    }
  }, [])

  const continueAi360TariffOrderBanner: Banner = useMemo(() => {
    return {
      key: BannerKey.ContinueAi360TariffOrderBanner,
      content: <ContinueAi360TariffOrderBanner />,
      priority: Priority.Medium,
      type: BannerType.Warning,
      locations: [BannerLocations.Dashboard],
    }
  }, [])

  const banners: Banner[] = useMemo(() => {
    const initialBanners = []

    if (installationId !== prevInstallationId || isStorageLoading || isUserSettingsLoading) return []

    /* showBannerStats is used here to not show the banner again to users who already disabled it */
    if (
      showDirectMarketingBanner &&
      !getIsBannerRemovedByUser(BannerKey.DirectMarketing, userSettings) &&
      !userSettings?.showBannerStats &&
      !isFlowTheme
    ) {
      initialBanners.push(directMarketingBanner)
    }
    if ((!isStorageOnline || (isFarm && !areAllClientsOnline)) && !getIsBannerRemovedByUser(BannerKey.OfflineInstallationBanner, userSettings)) {
      initialBanners.push(offlineInstallationBanner, offlineInstallationBannerServiceView)
    }
    if (isSunModeDisabledBannerVisible) {
      initialBanners.push(sunModeDisabledBanner)
    }
    if (
      isActiveDynamicPowerPriceAlertBannerVisible &&
      !getIsBannerRemovedByUser(BannerKey.ActiveVariableDynamicPowerPriceAlertBanner, userSettings)
    ) {
      initialBanners.push(activeVariableDynamicPowerPriceAlertBanner)
    }

    if (shouldShowEmsBanner && !getIsBannerRemovedByUser(BannerKey.DynamicEmsBanner, userSettings)) {
      initialBanners.push(emsPromotionBanner)
    }

    if (!hasInstallationWritingRights && !getIsBannerRemovedByUser(BannerKey.ReadOnlyBanner, userSettings)) {
      initialBanners.push(readOnlyBanner)
    }

    if (!hasEMobilityWritingRights) {
      initialBanners.push(readOnlyBannerEmobility)
    }

    if (!isFlowTheme && incompleteDevicesWithDeadlineApproaching.length > 0 && !isDeviceLinkLoading) {
      if (!isServiceUser) {
        initialBanners.push(installationStatementDeadlineOverviewBanner)
      }
      initialBanners.push(installationStatementDeadlineDashboardBanner)
    }

    if (!isCountryInfoLoading && !country) {
      initialBanners.push(sessionsInfoMissingBanner, wallboxesInfoMissingBanner, tariffsInfoMissingBanner)
    }

    if (eMobilityImplementationCheckStatus === EMobilityImplementationCheckStatus.Warning) {
      initialBanners.push(newEMobilityUnavailableBanner)
    }

    if (shouldShowEMobilityTourBanner && !getIsBannerRemovedByUser(BannerKey.EmobilityTourBanner, userSettings)) {
      initialBanners.push(eMobilityTourBanner)
    }

    if (!getIsBannerRemovedByUser(BannerKey.NewVehicleManufacturersBanner, userSettings) && !isFlowTheme) {
      initialBanners.push(newVehicleManufacturersBanner)
    }

    if (isDynamicEmsV2Accessible && !getIsBannerRemovedByUser(BannerKey.AI360ReleaseBanner, userSettings) && !isFlowTheme) {
      initialBanners.push(ai360ReleaseBanner)
    }

    if (showDirectMarketingBanner && !getIsBannerRemovedByUser(BannerKey.EonOfferBanner, userSettings) && !isFlowTheme) {
      initialBanners.push(eonBanner)
    }

    if (hasAi360TariffBannerAccess && !isFlowTheme && !getIsBannerRemovedByUser(BannerKey.Ai360TariffsBanner, userSettings)) {
      initialBanners.push(ai360TariffsBanner)
    }

    if (shouldShowContinueAi360TariffOrderBanner && !getIsBannerRemovedByUser(BannerKey.ContinueAi360TariffOrderBanner, userSettings)) {
      initialBanners.push(continueAi360TariffOrderBanner)
    }

    return initialBanners
  }, [
    installationId,
    prevInstallationId,
    isStorageLoading,
    isUserSettingsLoading,
    showDirectMarketingBanner,
    userSettings,
    isStorageOnline,
    isFarm,
    areAllClientsOnline,
    isSunModeDisabledBannerVisible,
    shouldShowEmsBanner,
    isActiveDynamicPowerPriceAlertBannerVisible,
    hasInstallationWritingRights,
    hasEMobilityWritingRights,
    directMarketingBanner,
    offlineInstallationBanner,
    sunModeDisabledBanner,
    activeVariableDynamicPowerPriceAlertBanner,
    emsPromotionBanner,
    readOnlyBanner,
    readOnlyBannerEmobility,
    installationStatementDeadlineOverviewBanner,
    installationStatementDeadlineDashboardBanner,
    isServiceUser,
    offlineInstallationBannerServiceView,
    country,
    sessionsInfoMissingBanner,
    tariffsInfoMissingBanner,
    wallboxesInfoMissingBanner,
    eMobilityImplementationCheckStatus,
    newEMobilityUnavailableBanner,
    shouldShowEMobilityTourBanner,
    eMobilityTourBanner,
    newVehicleManufacturersBanner,
    ai360ReleaseBanner,
    isDynamicEmsV2Accessible,
    incompleteDevicesWithDeadlineApproaching,
    isDeviceLinkLoading,
    isCountryInfoLoading,
    eonBanner,
    ai360TariffsBanner,
    hasAi360TariffBannerAccess,
    continueAi360TariffOrderBanner,
    shouldShowContinueAi360TariffOrderBanner,
  ])

  /**
   * General loading state for the initial banners
   */
  const isLoading =
    installationId !== prevInstallationId ||
    isStorageLoading ||
    isUserSettingsLoading ||
    isDeviceLinkLoading ||
    isSoftwareVersionLoading ||
    isEMobilityImplementationLoading ||
    isCountryInfoLoading

  return { banners, isLoading }
}

export const getIsBannerRemovedByUser = (bannerKey: BannerKey, userSettings?: UserSettings): boolean => {
  if (!userSettings) return true

  return Boolean(userSettings.removedBanners?.includes(bannerKey))
}

export default useBanner
